import React, { FC, useContext, useMemo, useState } from 'react';
import { ValidationRecord } from '../../validations/types';
import ValidationsControls from './ValidationsControls';
import {
  ComboFilterCategory,
  ComboFilterListSelector,
  TableViewLayout,
  useTableFilters,
  Select,
  ExtendedTableColumn,
  tableDateSortParameters,
  FlexGroup,
  Button,
  Link,
  Text,
  useTectonTheme,
  Table,
  FlexItem,
  EmptyProps,
} from '@lithos';
import { ValidationsContext } from './ValidationsContext';
import ValidationsColumnControls from './ValidationsColumnControls';
import { uniq } from 'lodash';
import { dateTimeFormat, timeZone } from '@lithos/DateUtils';
import ValidationsFlyout from './ValidationsFlyout';
import ResultBadge from './ResultBadge';
import { ExpectationResultEnum } from '../../../types/tecton_proto/dataobs/validation';
import { useUserSettings } from '../../context/UserSettingsContext';

enum ValidationsListColumnKeys {
  STATUS,
  FEATURE_VIEW,
  VALIDATION_PARAMETERS,
  DETAILS,
  COMPLETED_AT,
}

interface ValidationsListFilterState {
  status: string[];
  featureView: string[];
}

const ValidationsTableView: FC = () => {
  const {
    data,
    loadMore,
    filterState: contextFilterState,
    updateFilters,
    isLoading,
    hasMore,
  } = useContext(ValidationsContext);
  const [flyoutContent, setFlyoutContent] = useState<React.ReactNode | undefined>(undefined);
  const { theme } = useTectonTheme();
  const { workspace } = useUserSettings();
  const defaultFilterState: ValidationsListFilterState = useMemo(
    (): ValidationsListFilterState => ({
      status: [],
      featureView: [],
    }),
    []
  );

  const allValidOptions = useMemo(() => {
    return {
      Status: ['Passed', 'Failed', 'Unknown'],
      'Feature View': uniq(data.map((validation) => validation.featureViewName)),
    };
  }, [data]);

  const {
    hasAnyFilters,
    activeFilterCount,
    sortType,
    setSortType,
    filterState,
    handleResetFilters,
    handleFilterChange,
    activeHeading,
    setActiveHeading,
  } = useTableFilters<ValidationRecord, ValidationsListColumnKeys, ValidationsListFilterState>(defaultFilterState);

  const filterComponents: Partial<
    Record<
      ValidationsListColumnKeys,
      {
        filter: React.ReactNode;
        activeCount: () => number;
        filterCallback: (record: ValidationRecord) => boolean;
        name: string;
      }
    >
  > = useMemo(() => {
    const statusMap: Record<string, ExpectationResultEnum> = {
      Passed: ExpectationResultEnum.RESULT_PASSED,
      Failed: ExpectationResultEnum.RESULT_FAILED,
      Unknown: ExpectationResultEnum.RESULT_UNKNOWN,
    };
    return {
      [ValidationsListColumnKeys.STATUS]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.status}
            setSelected={(type) => {
              updateFilters({
                ...contextFilterState,
                statuses: type.map((status) => statusMap[status]),
              });
              handleFilterChange(filterComponents, 'status', type);
            }}
            items={['Passed', 'Failed', 'Unknown']}
            title={''}
          />
        ),
        activeCount: () => filterState.status.length,
        filterCallback: (record) => filterState.status.includes(record.status),
        name: 'status',
      },
      [ValidationsListColumnKeys.FEATURE_VIEW]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.featureView}
            setSelected={(type) => handleFilterChange(filterComponents, 'featureView', type)}
            icon={'FeatureView'}
            items={uniq(data.map((item) => item.featureViewName)).sort()}
            title={''}
          />
        ),
        activeCount: () => filterState.featureView.length,
        filterCallback: (record) => filterState.featureView.includes(record.featureViewName),
        name: 'featureView',
      },
    };
  }, [contextFilterState, data, filterState.featureView, filterState.status, handleFilterChange, updateFilters]);

  const categories: ComboFilterCategory[] = [
    {
      name: 'Status',
      icon: 'DataQuality',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'status', defaultFilterState.status);
      },
      activeCount: filterComponents[ValidationsListColumnKeys.STATUS]!.activeCount(),
      popover: filterComponents[ValidationsListColumnKeys.STATUS]!.filter,
      disabled: allValidOptions['Status'].length === 0,
    },
    {
      name: 'Feature View',
      icon: 'FeatureView',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'featureView', defaultFilterState.featureView);
      },
      activeCount: filterComponents[ValidationsListColumnKeys.FEATURE_VIEW]!.activeCount(),
      popover: filterComponents[ValidationsListColumnKeys.FEATURE_VIEW]!.filter,
      disabled: allValidOptions['Feature View'].length === 0,
    },
  ];

  const columns: ExtendedTableColumn<ValidationRecord & { id: string }>[] = [
    {
      name: 'Status',
      id: 'status',
      content: (item: ValidationRecord) => {
        return (
          <FlexGroup padding="0" alignItems="center">
            <ResultBadge result={item.status} />
            <Button
              size="s"
              icon="Information"
              type="secondary"
              onClick={() => {
                setFlyoutContent(<ValidationsFlyout validation={item} setFlyout={setFlyoutContent} />);
              }}
            />
          </FlexGroup>
        );
      },
      filterContent: filterComponents[ValidationsListColumnKeys.STATUS]!.filter,
      columnWidth: 's',
    },
    {
      name: 'Feature View',
      id: 'featureView',
      content: (item: ValidationRecord) => {
        return (
          <Link
            icon="FeatureView"
            name={item.featureViewName}
            url={`../${workspace}/features/${item.featureViewName}`}
          />
        );
      },
      filterContent: filterComponents[ValidationsListColumnKeys.FEATURE_VIEW]!.filter,
      columnWidth: 'l',
    },
    {
      name: 'Validation Parameters',
      id: 'validationParameters',
      content: (item: ValidationRecord) => {
        return <>{item.expectationName}</>;
      },
      columnWidth: 'l',
    },
    {
      name: 'Details',
      id: 'details',
      content: (item: ValidationRecord) => {
        return (
          <Text color={theme.v1.colors.text.dangerText} weight={'medium'}>
            {item.metricExpectationMetadata?.alertMessage}
          </Text>
        );
      },
      columnWidth: 'xl',
    },
    {
      name: 'Completed At',
      content: (item: ValidationRecord) => <>{dateTimeFormat(item.validationTime, timeZone.UTC)}</>,
      id: 'completedAt',
      columnWidth: 'm',
      ...tableDateSortParameters(sortType, 'validationTime', setSortType),
    },
  ];

  const selectableColumns = columns.map((column) => {
    return { name: column.name as string, visible: true };
  });

  const [columnState, setColumnState] = useState<{ name: string; visible: boolean }[]>(selectableColumns);

  const columnsSelector = (
    <Select
      groups={[
        {
          items: columnState.map((column) => {
            return {
              name: column.name,
              asToggle: true,
              onClick: () => {
                const newColumnState = columnState.map((selectableColumn) => {
                  if (selectableColumn.name !== column.name) {
                    return selectableColumn;
                  }

                  return {
                    name: selectableColumn.name,
                    visible: !selectableColumn.visible,
                  };
                });
                setColumnState && setColumnState(newColumnState);
              },
              selected: column.visible,
              selectable: true,
            };
          }),
        },
      ]}
      filterable={false}
    />
  );

  const filteredColumns = columns
    .map((column) => {
      const matching = columnState.find((stateColumn) => stateColumn.name === column.name);
      if (!matching) {
        return;
      }
      if (matching?.visible !== true) {
        return;
      }

      const asColumn: ExtendedTableColumn<ValidationRecord> = {
        ...column,
        isActive: column.id === activeHeading,
        setIsActive: (arg) => {
          if (arg) {
            setActiveHeading(column.id as ValidationsListColumnKeys);
            return;
          }
          setActiveHeading(undefined);
        },
        hideCallback: () => {
          const newColumnState = columnState.map((selectableColumn) => {
            if (selectableColumn.name !== column.name) {
              return selectableColumn;
            }

            return {
              name: selectableColumn.name,
              visible: !selectableColumn.visible,
            };
          });

          setColumnState && setColumnState(newColumnState);
        },
        columnsContent: columnsSelector,
      };

      return asColumn;
    })
    .filter((column) => column !== undefined) as ExtendedTableColumn<ValidationRecord>[];

  const filterEmpty: EmptyProps | undefined = isLoading
    ? undefined
    : {
        actions: <></>,
        placement: 'full-page',
        useCase: 'empty',
        title: 'No Results',
        description: 'No results were found. Try resetting the filters or adjusting the time range.',
      };

  return (
    <>
      <TableViewLayout
        searchInput={
          <ValidationsControls
            hasAnyFilters={hasAnyFilters}
            activeFilterCount={activeFilterCount}
            isLoading={isLoading}
            handleResetFilters={() => {
              updateFilters({
                ...contextFilterState,
                statuses: [],
                featureViews: [],
              });
              handleResetFilters();
            }}
            categories={categories}
          />
        }
        filterAndColumnControls={<ValidationsColumnControls columnsSelector={columnsSelector} />}
        table={
          <FlexGroup direction="column">
            <FlexItem shrink={1} grow={1}>
              <Table columns={filteredColumns} items={data} frozenFirstColumn emptyProps={filterEmpty} />
              {flyoutContent}
            </FlexItem>
            <FlexItem shrink={0} grow={0}>
              <FlexGroup justifyContent="center">
                {!!loadMore && (
                  <Button
                    type="secondary"
                    label="Load More Rows"
                    onClick={() => {
                      loadMore();
                    }}
                    state={hasMore && !isLoading ? 'default' : 'disabled'}
                  />
                )}
              </FlexGroup>
            </FlexItem>
          </FlexGroup>
        }
      />
    </>
  );
};

export default ValidationsTableView;
